import { Head, useForm } from "@inertiajs/react"
import { FileTextIcon } from "@radix-ui/react-icons"

import { AppShell } from "@/components/app-shell"
import { Button } from "@/components/ui/button"

function errorText(errors, field) {
  const error = errors[field]
  return Array.isArray(error) ? error.join(", ") : error
}

function slugFromName(value) {
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function selectedFileNames(files) {
  if (files.length === 0) return "No PDFs selected"

  return files.map((file) => file.name).join(", ")
}

export default function NewOrganizationPage({ form = {}, errors = {} }) {
  const organizationForm = useForm({
    name: form.name || "",
    files: []
  })
  const previewSlug = slugFromName(organizationForm.data.name)

  return (
    <>
      <Head title="Create organization" />

      <AppShell
        title="Create your knowledge portal"
        subtitle="Name the organization and add the PDFs that should be searchable first."
      >
        <section className="rounded-[1.75rem] bg-white/82 p-6 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
          <form
            className="grid gap-6"
            onSubmit={(event) => {
              event.preventDefault()
              organizationForm.transform((data) => ({
                organization: {
                  name: data.name,
                  files: data.files
                }
              }))
              organizationForm.post("/orgs", {
                forceFormData: true
              })
            }}
          >
            <div>
              <label className="text-sm font-medium text-[var(--color-charcoal-blue)]" htmlFor="organization-name">
                Organization name
              </label>
              <input
                autoComplete="organization"
                autoFocus
                className="input-field mt-2"
                id="organization-name"
                onChange={(event) => organizationForm.setData("name", event.target.value)}
                value={organizationForm.data.name}
              />
              {previewSlug ? (
                <p className="mt-2 text-sm text-[var(--color-text-muted)]">Portal URL: /orgs/{previewSlug}</p>
              ) : null}
              {errorText(errors, "name") ? (
                <p className="mt-2 text-sm text-[var(--color-danger)]">{errorText(errors, "name")}</p>
              ) : null}
              {errorText(errors, "slug") ? (
                <p className="mt-2 text-sm text-[var(--color-danger)]">{errorText(errors, "slug")}</p>
              ) : null}
            </div>

            <div>
              <label className="text-sm font-medium text-[var(--color-charcoal-blue)]" htmlFor="knowledge-pdf-files">
                Documents to index
              </label>
              <input
                accept="application/pdf"
                className="input-field mt-2"
                id="knowledge-pdf-files"
                multiple
                onChange={(event) => organizationForm.setData("files", Array.from(event.target.files || []))}
                type="file"
              />
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] px-3 py-2 text-sm text-[var(--color-text-muted)]">
                <FileTextIcon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-charcoal-blue)]" />
                <span className="min-w-0 break-words">{selectedFileNames(organizationForm.data.files)}</span>
              </div>
              {errorText(errors, "file") ? (
                <p className="mt-2 text-sm text-[var(--color-danger)]">{errorText(errors, "file")}</p>
              ) : null}
            </div>

            <div>
              <Button
                disabled={
                  organizationForm.processing ||
                  organizationForm.data.name.trim().length === 0 ||
                  organizationForm.data.files.length === 0
                }
                type="submit"
              >
                Create and start indexing
              </Button>
            </div>
          </form>
        </section>
      </AppShell>
    </>
  )
}
